import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The animated backdrop behind the signed-out pages. C18.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for by the product owner on 26 Aug 2026: react-bits' `GradientWaves`, full width, behind the
 * card on sign-in, forgot-password and the reset pages. The shader itself is vendored verbatim in
 * `gradient-waves.tsx`; **everything that makes it safe to mount is here**, so that file stays
 * diffable against upstream.
 *
 * Three things it does that this repository cannot ship unguarded:
 *
 * ⚠️⚠️ **1. IT ASSUMES WEBGL2, AND jsdom HAS NONE — SO THE ROUTE GATE WOULD HAVE GONE RED.**
 * `new Renderer({ webgl: 2 })` runs in an unguarded effect, and `routes.test.tsx` renders `/sign-in`
 * like every other address. The same absence is real outside the tests: a locked-down browser, a
 * VM with no GPU passthrough, or a driver on the blocklist all yield no context — and the login
 * screen is the one page where "renders nothing" means "nobody can get in". So the shader is
 * mounted **only after a real context has been obtained and thrown away**, and the fallback below
 * is not a degraded state, it is the mockup's own background.
 *
 * ⚠️⚠️ **2. IT ANIMATES FOREVER, AND `prefers-reduced-motion` IS NOT A PREFERENCE TO WEIGH.**
 * `packages/ui`'s base stylesheet already honours the query for every transition in the product
 * (`tokens.css`, the mockup's own `@media (prefers-reduced-motion: reduce)` block) — a full-screen
 * raymarched swell is the single most motion-heavy thing in it, and a CSS rule cannot stop a
 * `requestAnimationFrame` loop. Somebody with vestibular sensitivity gets the still gradient.
 *
 * ⚠️ **3. `ogl` IS 413 KB UNPACKED, ON THE ONE PAGE THAT LOADS BEFORE ANYTHING ELSE.** `lazy` keeps
 * it out of the entry chunk, so the card, the fields and the button are interactive at the same
 * moment they were before this landed and the waves arrive when they arrive. Nothing waits on it —
 * `Suspense` falls back to the static backdrop, which is what is already painted.
 *
 * ⚠️ **The card is opaque (`--card`), so text contrast is unaffected by any of this.** The page
 * FOOTER is not, and `auth-frame.tsx` carries the change that keeps it legible.
 */

/**
 * ⚠️ **A default import, because the vendored file has no named export.** Upstream ends with
 * `export default GradientWaves`, and rewriting that would be the first divergence in a file whose
 * value is being diffable.
 */
const GradientWaves = lazy(async () => import('./gradient-waves'));

/**
 * The product owner's settings from the Background Studio, verbatim.
 *
 * ⚠️ **Kept as one object rather than spread across the JSX** so a re-tune is one paste from the
 * tool, and so the two values that are ours rather than theirs are visible as exceptions: the studio
 * emits a fixed `1080 × 1080` box, and this is full-bleed instead (`mouseInteraction` was already
 * `false` in what was sent).
 */
const SETTINGS = {
  /**
   * ⚠️⚠️ **WHITE, WHERE THE BACKGROUND STUDIO SENT `#6773ff`.** `horizonColor` is not an
   * accent — it is the sky the waves fade into, so it fills every pixel the swell does not reach.
   * At the studio's blue that is the whole upper two-thirds of the page: the card ended up floating
   * on a saturated blue field rather than on the product's own near-white surface, which is not what
   * the effect looks like in the studio's own 1080×1080 preview (there the box is small enough that
   * the horizon barely shows). Changed by the product owner on sight.
   *
   * The two wave colours are untouched — they are what actually reads as the effect.
   */
  horizonColor: '#ffffff',
  waveColor: '#516bff',
  crestColor: '#9b67ff',
  speed: 0.4,
  amplitude: 2.5,
  waveScale: 0.6,
  waveRatio: 1.1,
  swell: 26.5,
  turbulence: 26.5,
  tilt: 1.03,
  zoom: 1.3,
  height: 9.0,
  fogDepth: 35,
  detail: 'high',
  brightness: 1,
  opacity: 0.7,
  grain: true,
  grainIntensity: 0.05,
  mouseInteraction: false,
  parallaxStrength: 0.5,
} as const;

/**
 * Whether this browser can actually run the thing.
 *
 * ⚠️⚠️ **TWO STEPS, AND BOTH ARE LOAD-BEARING FOR DIFFERENT REASONS.**
 *
 * The **constructor check** is a cheap gate that is decisive when it fails: no
 * `WebGL2RenderingContext` on `window` means no WebGL2, full stop. It is first because asking jsdom
 * for a canvas context makes it emit *"Not implemented:
 * HTMLCanvasElement.prototype.getContext"* through its virtual console — caught by the `try` below
 * and therefore harmless, but printed once per auth-screen render across the whole suite. A guard
 * whose correct behaviour fills the test log with errors is one somebody eventually silences.
 *
 * The **real context** is what actually decides it, because the constructor existing proves nothing:
 * a blocklisted driver, a headless browser with no GL backend, or a tab that has hit the live-context
 * limit all expose the constructor and then refuse to hand one over. `hard-won.md`: *"measure the
 * thing, not a proxy for it"* — so the proxy is only allowed to say **no**, never **yes**.
 *
 * ⚠️ The context is released immediately; holding it would count against that same limit. And the
 * call is wrapped because `getContext` **throws** rather than returning null in some hardened
 * browsers.
 */
function canRenderWaves(): boolean {
  if (typeof document === 'undefined') return false;
  /* The cheap half: decisive on failure, meaningless on success. See above. */
  if (typeof WebGL2RenderingContext === 'undefined') return false;
  try {
    const probe = document.createElement('canvas');
    const gl = probe.getContext('webgl2');
    if (gl === null) return false;
    /* Hand it straight back. `WEBGL_lose_context` is the supported way; its absence is not a
     * failure, the canvas is unreferenced either way. */
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

function prefersReducedMotion(): boolean {
  /* ⚠️ `matchMedia` is absent in jsdom unless a test stubs it, so its absence must read as "no
   * preference expressed" rather than throwing. The capability check above is what keeps the shader
   * out of the tests; this must not be the thing that decides it. */
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AuthBackdrop(): ReactNode {
  /**
   * ⚠️⚠️ **`false` UNTIL AN EFFECT SAYS OTHERWISE, WHICH IS NOT THE SAME AS CHECKING DURING
   * RENDER.** Deciding in the render body would run the probe on every render, and — more to the
   * point — would make the first paint depend on a GPU call. The page paints the static backdrop
   * immediately and upgrades itself, which is also exactly what somebody on a slow machine should
   * get.
   */
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    if (!canRenderWaves()) return;
    setEnabled(true);
  }, []);

  return (
    /**
     * ⚠️⚠️ **`z-0`, NOT `-z-10` — SEE `auth-frame.tsx`.** A negative z-index child paints behind
     * its positioned parent's own background, and that parent has one, so the first version of this
     * was invisible: gradient, canvas and all. The frame lifts its content to `z-10` instead.
     *
     * ⚠️ **`fixed inset-0`, so it is full-bleed regardless of how tall the card gets** — the studio's
     * snippet is a fixed 1080px square, which would have left the waves as a panel behind a centred
     * box rather than a background. `-z-10` puts it under the card without either of them needing a
     * stacking context of its own.
     *
     * ⚠️ **`aria-hidden` and `pointer-events-none`.** It is decoration: it must not be announced, and
     * it must never intercept a click meant for the form. `mouseInteraction` is off, so the shader
     * wants no pointer events either.
     */
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      {/**
       * ⚠️⚠️ **THE STATIC LAYER IS ALWAYS PAINTED, AND THE SHADER SITS ON TOP OF IT.** It is not an
       * `else` branch: the shader's own `opacity: 0.7` means it is partly transparent by design, and
       * on a page whose background would otherwise be whatever is behind the canvas that reads as a
       * washed-out flicker on first frame. This is also the whole backdrop when WebGL is unavailable
       * or motion is reduced, which is why it uses the same three colours rather than a grey.
       */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(120% 90% at 50% 100%, ${SETTINGS.crestColor} 0%, ${SETTINGS.waveColor} 38%, ${SETTINGS.horizonColor} 100%)`,
          /**
           * ⚠️⚠️ **THE SAME `opacity` THE SHADER USES, SO BOTH PATHS COMPOSITE OVER
           * `--sidebar` IDENTICALLY.** Without it the fallback is the three colours at full
           * saturation while the shader is those colours at 70% over the page's near-white — so the
           * static layer read as a far heavier, more saturated version of the effect, and it was
           * visible as a flash in the moment before the lazy chunk arrived. Deriving it from
           * `SETTINGS` rather than repeating `0.7` means a re-tune from the studio moves both.
           */
          opacity: SETTINGS.opacity,
        }}
      />

      {enabled ? (
        <Suspense fallback={null}>
          <GradientWaves {...SETTINGS} />
        </Suspense>
      ) : null}
    </div>
  );
}
